import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Send, Loader2, Book, MessageSquare, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import BibleVerse from "@/components/BibleVerse";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const BibleStudy = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verseReference, setVerseReference] = useState("");
  const [activeTab, setActiveTab] = useState("chat");
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasProcessedChapterRef = useRef(false);

  const studyTopics = [
    { 
      title: t("biblestudy.topics.faith"),
      verses: language === "ru" 
        ? ["Евреям 11:1", "Притчи 3:5-6", "Римлянам 10:17"]
        : language === "es"
        ? ["Hebreos 11:1", "Proverbios 3:5-6", "Romanos 10:17"]
        : ["Hebrews 11:1", "Proverbs 3:5-6", "Romans 10:17"]
    },
    { 
      title: t("biblestudy.topics.love"),
      verses: language === "ru"
        ? ["1 Коринфянам 13:4-8", "Иоанна 13:34-35", "1 Иоанна 4:7-8"]
        : language === "es"
        ? ["1 Corintios 13:4-8", "Juan 13:34-35", "1 Juan 4:7-8"]
        : ["1 Corinthians 13:4-8", "John 13:34-35", "1 John 4:7-8"]
    },
    { 
      title: t("biblestudy.topics.prayer"),
      verses: language === "ru"
        ? ["Матфея 6:9-13", "Филиппийцам 4:6-7", "1 Фессалоникийцам 5:16-18"]
        : language === "es"
        ? ["Mateo 6:9-13", "Filipenses 4:6-7", "1 Tesalonicenses 5:16-18"]
        : ["Matthew 6:9-13", "Philippians 4:6-7", "1 Thessalonians 5:16-18"]
    },
    { 
      title: t("biblestudy.topics.salvation"),
      verses: language === "ru"
        ? ["Ефесянам 2:8-9", "Римлянам 3:23-24", "Иоанна 3:16-17"]
        : language === "es"
        ? ["Efesios 2:8-9", "Romanos 3:23-24", "Juan 3:16-17"]
        : ["Ephesians 2:8-9", "Romans 3:23-24", "John 3:16-17"]
    },
    { 
      title: t("biblestudy.topics.wisdom"),
      verses: language === "ru"
        ? ["Иакова 1:5", "Псалом 119:105", "Притчи 2:6"]
        : language === "es"
        ? ["Santiago 1:5", "Salmos 119:105", "Proverbios 2:6"]
        : ["James 1:5", "Psalm 119:105", "Proverbs 2:6"]
    },
    { 
      title: t("biblestudy.topics.peace"),
      verses: language === "ru"
        ? ["Иоанна 14:27", "Филиппийцам 4:7", "2 Фессалоникийцам 3:16"]
        : language === "es"
        ? ["Juan 14:27", "Filipenses 4:7", "2 Tesalonicenses 3:16"]
        : ["John 14:27", "Philippians 4:7", "2 Thessalonians 3:16"]
    },
  ];

  const popularVerses = language === "ru"
    ? ["Иоанна 3:16-17", "Псалом 23", "Римлянам 8:28", "Филиппийцам 4:13", "Иеремия 29:11", "Матфея 28:19-20"]
    : language === "es"
    ? ["Juan 3:16-17", "Salmos 23", "Romanos 8:28", "Filipenses 4:13", "Jeremías 29:11", "Mateo 28:19-20"]
    : ["John 3:16-17", "Psalm 23", "Romans 8:28", "Philippians 4:13", "Jeremiah 29:11", "Matthew 28:19-20"];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Clear messages when language changes
  useEffect(() => {
    if (messages.length > 0) {
      setMessages([]);
    }
  }, [language]);

  // Handle chapter reference from navigation
  useEffect(() => {
    const state = location.state as { chapterReference?: string } | null;
    if (state?.chapterReference && !hasProcessedChapterRef.current) {
      hasProcessedChapterRef.current = true;
      setActiveTab("chat");
      const question = language === "ru"
        ? `Расскажите мне о ${state.chapterReference}`
        : language === "es"
        ? `Háblame sobre ${state.chapterReference}`
        : `Tell me about ${state.chapterReference}`;
      streamChat(question);
    }
  }, [location.state]);

  const streamChat = async (userMessage: string) => {
    const newMessages = [...messages, { role: "user" as const, content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bible-study-chat`;
      
      // Get auth token if user is logged in, otherwise use anon key
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ messages: newMessages, language }),
      });

      if (!resp.ok) {
        if (resp.status === 429) {
          toast({
            title: "Rate Limit",
            description: "Too many requests. Please wait a moment.",
            variant: "destructive",
          });
          return;
        }
        if (resp.status === 402) {
          toast({
            title: "Credits Required",
            description: "Please add credits to continue using AI features.",
            variant: "destructive",
          });
          return;
        }
        throw new Error("Failed to start stream");
      }

      if (!resp.body) throw new Error("No response body");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;
      let assistantContent = "";

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) => 
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: "assistant", content: assistantContent }];
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Error",
        description: "Failed to get response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    await streamChat(userMessage);
  };

  const handleTopicClick = (topic: string) => {
    const question = language === "ru"
      ? `Можете ли вы помочь мне понять, что Библия учит о ${topic}?`
      : language === "es"
      ? `¿Puedes ayudarme a entender lo que la Biblia enseña sobre ${topic}?`
      : `Can you help me understand what the Bible teaches about ${topic}?`;
    setActiveTab("chat");
    streamChat(question);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
              <Book className="h-8 w-8" />
              {t("biblestudy.title")}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t("biblestudy.subtitle")}
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="chat">
                <MessageSquare className="h-4 w-4 mr-2" />
                {t("biblestudy.tab.chat")}
              </TabsTrigger>
              <TabsTrigger value="verse">
                <Search className="h-4 w-4 mr-2" />
                {t("biblestudy.tab.verse")}
              </TabsTrigger>
              <TabsTrigger value="topics">
                <BookOpen className="h-4 w-4 mr-2" />
                {t("biblestudy.tab.topics")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="chat" className="space-y-4">
              <Card className="h-[600px] flex flex-col">
                <CardHeader>
                  <CardTitle>{t("biblestudy.chat.title")}</CardTitle>
                  <CardDescription>
                    {t("biblestudy.chat.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col min-h-0">
                  <ScrollArea className="flex-1 pr-4" ref={scrollRef}>
                    {messages.length === 0 ? (
                      <div className="text-center text-muted-foreground py-12">
                        <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>{t("biblestudy.chat.empty")}</p>
                        <div className="mt-6 space-y-2">
                          <p className="text-sm font-medium">{t("biblestudy.chat.tryasking")}</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {["What does the Bible say about faith?", "Explain John 3:16", "How should I pray?"].map((q) => (
                              <Button
                                key={q}
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setInput(q);
                                  streamChat(q);
                                }}
                              >
                                {q}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 pb-4">
                        {messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                msg.role === "user"
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-card border border-border text-foreground"
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  <form onSubmit={handleSend} className="flex gap-2 mt-4 pt-4 border-t">
                    <Textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={t("biblestudy.chat.placeholder")}
                      className="resize-none"
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend(e);
                        }
                      }}
                    />
                    <Button type="submit" disabled={isLoading || !input.trim()} size="icon" className="h-auto">
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="verse" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t("biblestudy.verse.title")}</CardTitle>
                  <CardDescription>
                    {t("biblestudy.verse.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder={t("biblestudy.verse.placeholder")}
                      value={verseReference}
                      onChange={(e) => setVerseReference(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                  {verseReference && <BibleVerse reference={verseReference} />}
                  
                  <div className="pt-4">
                    <h3 className="font-semibold mb-3">{t("biblestudy.verse.popular")}</h3>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {popularVerses.map((ref) => (
                        <Button
                          key={ref}
                          variant="outline"
                          onClick={() => setVerseReference(ref)}
                          className="justify-start"
                        >
                          <BookOpen className="h-4 w-4 mr-2" />
                          {ref}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="topics" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t("biblestudy.topics.title")}</CardTitle>
                  <CardDescription>
                    {t("biblestudy.topics.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    {studyTopics.map((topic) => (
                      <Card key={topic.title} className="hover:shadow-md transition-shadow">
                        <CardHeader>
                          <CardTitle className="text-lg">{topic.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {topic.verses.map((verse) => (
                            <BibleVerse key={verse} reference={verse} compact />
                          ))}
                          <Button
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => handleTopicClick(topic.title)}
                          >
                            {t("biblestudy.topics.ask")} {topic.title}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BibleStudy;
